import { collection, collectionGroup, doc, getDoc, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { db } from '../../../firebase';
import { createApiCall } from '../../../services/apiClient';

export interface UserTriggerLogStats {
    userId: string;
    email: string | null;
    displayName: string | null;
    triggerLogCount: number;
    memberSince: Date;
}

export interface UserSttStats {
    userId: string;
    email: string | null;
    displayName: string | null;
    sttSessionCount: number;
    memberSince: Date;
    lastUsedAt: Date | null;
}

export interface UserVoiceStats {
    userId: string;
    email: string | null;
    displayName: string | null;
    voiceConversationCount: number;
    totalDurationSecs: number;
    memberSince: Date;
    lastUsedAt: Date | null;
}

export interface UserChatHistoryStats {
    userId: string;
    email: string | null;
    displayName: string | null;
    normalChatCount: number;
    questChatCount: number;
    memberSince: Date;
    lastUsedAt: Date | null;
}

interface ChatHistoryLeaderboardResponse {
    users: {
        userId: string;
        normalChatCount: number;
        questChatCount: number;
        lastUpdatedAtMs: number | null;
    }[];
    totalUsers: number;
}

// Admins cannot read user_chat_history through the Firestore rules, so the API counts the chats.
const getChatHistoryLeaderboard = createApiCall<{ limit: number }, ChatHistoryLeaderboardResponse>(
    'adminGetChatHistoryLeaderboard',
    { timeoutMs: 60_000 },
);

export class StatsService {
    private static instance: StatsService;

    static getInstance(): StatsService {
        if (!StatsService.instance) {
            StatsService.instance = new StatsService();
        }
        return StatsService.instance;
    }

    async getTopUsersByTriggerLogCount(limitCount: number = 20): Promise<UserTriggerLogStats[]> {
        try {
            // Use collectionGroup to query all trigger_logs across all users
            const triggerLogsQuery = collectionGroup(db, 'trigger_logs');
            const snapshot = await getDocs(triggerLogsQuery);

            // Group by parent userId and count
            const countsByUser = new Map<string, number>();
            snapshot.docs.forEach(doc => {
                // Get userId from the document path: user_info/{userId}/trigger_logs/{logId}
                const userId = doc.ref.parent.parent?.id;
                if (userId) {
                    countsByUser.set(userId, (countsByUser.get(userId) || 0) + 1);
                }
            });

            // Convert to array and sort by count descending
            const sortedUserIds = Array.from(countsByUser.entries())
                .sort((a, b) => b[1] - a[1])
                .slice(0, limitCount)
                .map(([userId, count]) => ({ userId, count }));

            // Fetch user details for top users
            const userStatsPromises = sortedUserIds.map(async ({ userId, count }) => {
                const userDocRef = doc(db, 'user_info', userId);
                const userDoc = await getDoc(userDocRef);
                
                if (!userDoc.exists()) {
                    return {
                        userId,
                        email: null,
                        displayName: null,
                        triggerLogCount: count,
                        memberSince: new Date(),
                    };
                }

                const userData = userDoc.data();
                return {
                    userId,
                    email: userData?.email || null,
                    displayName: userData?.displayName || null,
                    triggerLogCount: count,
                    memberSince: userData?.createdAt?.toDate() || new Date(),
                };
            });

            const userStats = await Promise.all(userStatsPromises);
            return userStats;
        } catch (error) {
            console.error('Error fetching top users by trigger log count:', error);
            throw error;
        }
    }
    async getTopUsersBySttSessionCount(limitCount: number = 20): Promise<UserSttStats[]> {
        try {
            const actionCountsQuery = query(
                collection(db, 'user_action_counts'),
                orderBy('sttSessionStarted', 'desc'),
                limit(limitCount),
            );
            const snapshot = await getDocs(actionCountsQuery);

            const userStatsPromises = snapshot.docs.map(async (actionDoc) => {
                const userId = actionDoc.id;
                const actionData = actionDoc.data();
                const sttSessionCount = actionData.sttSessionStarted ?? 0;
                const lastUsedAt = actionData.sttSessionStartedLastUsedAt?.toDate() ?? null;

                const userDocRef = doc(db, 'user_info', userId);
                const userDoc = await getDoc(userDocRef);

                if (!userDoc.exists()) {
                    return {
                        userId,
                        email: null,
                        displayName: null,
                        sttSessionCount,
                        memberSince: new Date(),
                        lastUsedAt,
                    };
                }

                const userData = userDoc.data();
                return {
                    userId,
                    email: userData?.email || null,
                    displayName: userData?.displayName || null,
                    sttSessionCount,
                    memberSince: userData?.createdAt?.toDate() || new Date(),
                    lastUsedAt,
                };
            });

            return await Promise.all(userStatsPromises);
        } catch (error) {
            console.error('Error fetching top users by STT session count:', error);
            throw error;
        }
    }

    async getTopUsersByVoiceConversationCount(limitCount: number = 20): Promise<UserVoiceStats[]> {
        try {
            const actionCountsQuery = query(
                collection(db, 'user_action_counts'),
                orderBy('voiceConversationStarted', 'desc'),
                limit(limitCount),
            );
            const snapshot = await getDocs(actionCountsQuery);

            const userStatsPromises = snapshot.docs.map(async (actionDoc) => {
                const userId = actionDoc.id;
                const actionData = actionDoc.data();
                const voiceConversationCount = actionData.voiceConversationStarted ?? 0;
                const totalDurationSecs = actionData.voiceConversationDurationSecs ?? 0;
                const lastUsedAt = actionData.voiceConversationStartedLastUsedAt?.toDate() ?? null;

                const userDocRef = doc(db, 'user_info', userId);
                const userDoc = await getDoc(userDocRef);

                if (!userDoc.exists()) {
                    return {
                        userId,
                        email: null,
                        displayName: null,
                        voiceConversationCount,
                        totalDurationSecs,
                        memberSince: new Date(),
                        lastUsedAt,
                    };
                }

                const userData = userDoc.data();
                return {
                    userId,
                    email: userData?.email || null,
                    displayName: userData?.displayName || null,
                    voiceConversationCount,
                    totalDurationSecs,
                    memberSince: userData?.createdAt?.toDate() || new Date(),
                    lastUsedAt,
                };
            });

            return await Promise.all(userStatsPromises);
        } catch (error) {
            console.error('Error fetching top users by voice conversation count:', error);
            throw error;
        }
    }

    async getTopUsersByChatCount(limitCount: number = 20): Promise<UserChatHistoryStats[]> {
        try {
            const { users } = await getChatHistoryLeaderboard({ limit: limitCount });

            const userStatsPromises = users.map(async (entry) => {
                const lastUsedAt = entry.lastUpdatedAtMs !== null ? new Date(entry.lastUpdatedAtMs) : null;
                const userDoc = await getDoc(doc(db, 'user_info', entry.userId));
                const userData = userDoc.exists() ? userDoc.data() : undefined;

                return {
                    userId: entry.userId,
                    email: userData?.email || null,
                    displayName: userData?.displayName || null,
                    normalChatCount: entry.normalChatCount,
                    questChatCount: entry.questChatCount,
                    memberSince: userData?.createdAt?.toDate() || new Date(),
                    lastUsedAt,
                };
            });

            return await Promise.all(userStatsPromises);
        } catch (error) {
            console.error('Error fetching top users by chat count:', error);
            throw error;
        }
    }
}

export const statsService = StatsService.getInstance();
